import {
  collectAllScopeIds,
  collectScopeIdsUnder,
  findFirstScopeIdByType,
  projectScopeSelection,
  selectedScopeAssetNames,
} from "../utils/scopeTree";
import { useEffect, useMemo, useState } from "react";
import {
  fetchAccessibleOrganizationsTree,
  fetchItComplianceOrgScopeTree,
} from "../services";
import { useAuthStore } from "../../../../stores/authStore";
import type { ScopeNode, ScopeTreeResult } from "../types/scope";

/** Fetches the org → department → division → application → asset hierarchy
 * that backs the "Group" scope filter shared by Overview / Vulnerability
 * Management / Configuration Management. Re-fetches when the active
 * organization changes. */
export function useOrganizationScopeTree(): ScopeTreeResult {
  const organizationId = useAuthStore((s) => s.user?.orgId);
  const [data, setData] = useState<ScopeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!organizationId) {
      setData([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchItComplianceOrgScopeTree(organizationId)
      .then((tree) => {
        if (cancelled) return;
        setData(Array.isArray(tree) ? tree : []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to load organization scope"),
        );
        setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, reloadKey]);

  return { data, loading, error, refresh: () => setReloadKey((k) => k + 1) };
}

/** Fetches the current user's accessible-organizations tree
 * (`/users/current/accessible-organizations/tree`) — used by the
 * Vulnerability Management and Configuration Management top filter bars'
 * "Group" scope picker instead of the department-hierarchy endpoint. */
export function useAccessibleOrganizationsTree(options?: { enabled?: boolean }): ScopeTreeResult {
  const enabled = options?.enabled ?? true;
  const [data, setData] = useState<ScopeNode[]>([]);
  // Starts true only when enabled: this avoids callers acting on empty initial tree when enabled,
  // but prevents unnecessary loading states when disabled.
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAccessibleOrganizationsTree(reloadKey > 0)
      .then((tree) => {
        if (cancelled) return;
        setData(Array.isArray(tree) ? tree : []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to load organization scope"),
        );
        setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, reloadKey]);

  return { data, loading, error, refresh: () => setReloadKey((k) => k + 1) };
}

/** Seeds the scope picker with the first node of `defaultScopeType` and all
 * descendants as soon as the scope-tree API settles. Returns `seeded`, which
 * stays false until the filter API has resolved and the default has been
 * applied — callers gate their charts on it so nothing fetches before a scope
 * exists. Falls back to the first root and its descendants when the requested
 * type isn't present. */
export function useInitialApplicationScope(
  scopeTree: ScopeTreeResult,
  apply: (ids: string[]) => void,
  defaultScopeType: ScopeNode["type"] = "application",
  selectionMode: "first" | "all" = "first",
): boolean {
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (seeded || scopeTree.loading) return;
    if (scopeTree.data.length) {
      const nextIds =
        selectionMode === "all"
          ? collectAllScopeIds(scopeTree.data)
          : (() => {
              const defaultScopeId = findFirstScopeIdByType(
                scopeTree.data,
                defaultScopeType,
              );
              return collectScopeIdsUnder(
                scopeTree.data,
                defaultScopeId ?? scopeTree.data[0].id,
              );
            })();
      apply(nextIds);
    }
    setSeeded(true);
    // `apply` is a fresh closure each render; the `seeded` guard makes it run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeTree.loading, scopeTree.data, seeded]);

  return seeded;
}

/** Unified hook that projects draft asset names and applied scope projections (asset names, app codes, app names) efficiently. */
export function useScopeSelections(
  tree: ScopeNode[],
  draftScope: string[],
  appliedScope: string[],
) {
  const draftAssets = useMemo(
    () => selectedScopeAssetNames(tree, draftScope),
    [tree, draftScope],
  );

  const {
    assetNames: appliedAssets,
    appCodes: appliedAppCodes,
    appNames: appliedAppNames,
  } = useMemo(
    () => projectScopeSelection(tree, appliedScope),
    [tree, appliedScope],
  );

  return {
    draftAssets,
    appliedAssets,
    appliedAppCodes,
    appliedAppNames,
  };
}
